import {
  createRouter,
  createWebHistory,
  type NavigationFailure,
  type RouteLocationGeneric,
  type RouteLocationNormalized,
  type RouteLocationNormalizedLoaded,
  type RouteRecordRaw
} from 'vue-router'
import { trackPage } from '@/services/analytics-service'
import { useUserStore } from '@/stores/user'
import { useSavingsStore } from '@/stores/savings'
import { i18nService } from '@accor/customer-vue-services'
import { storeToRefs } from 'pinia'
import type { SUBSCRIPTION_NAME } from '@/constants/subscriptions'
import { aabRoutes } from '@/router/aab.routes'
import {
  ACCOUNT_ACTIVITY_PAGE_NAME,
  ACCOUNT_ALL_SIGNATURE_PLAN_MODIFICATION_PAGE_NAME,
  ACCOUNT_BOOKINGS_PAGE_NAME,
  ACCOUNT_LOYALTY_PAGE_NAME,
  ACCOUNT_MY_INFORMATION_PAGE_NAME,
  ACCOUNT_STAY_PREFERENCES_PAGE_NAME,
  ACCOUNT_YOUR_PARTNER_BENEFITS_PAGE_NAME,
  ACCOUNT_HUB_SUBSCRIPTION_PAGE_NAME,
  ACCOUNT_PROFILE_IDENTITY_DOCUMENTS_PAGE_NAME,
  SECURITY_SETTINGS_PAGE_NAME
} from '@/constants/gtm-page-names'

declare module 'vue-router' {
  interface RouteMeta {
    gtmId?: string
    pageName?: string
    isVue2?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    name: 'locale',
    path: '/:locale?',
    beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      return await i18nService.routeHandler(to, from)
    },
    children: [
      {
        path: 'm-nav',
        name: 'mobile-nav',
        component: () => import('../views/NavView.vue')
      },
      {
        path: 'my-bookings/:section?',
        name: 'myBookings',
        meta: {
          title: 'myBookings.section',
          gtmId: 'GTM_PAGE_MY_BOOKINGS',
          pageName: ACCOUNT_BOOKINGS_PAGE_NAME
        },
        component: () => import('../views/MyBookings.vue')
      },
      {
        path: 'global-transaction-history',
        name: 'globalTransactionHistory',
        meta: {
          gtmId: 'GTM_ACCOUNT_ACTIVITY',
          pageName: ACCOUNT_ACTIVITY_PAGE_NAME
        },
        component: () => import('../views/GlobalTransactionHistory.vue')
      },
      {
        path: 'my-loyalty-program',
        name: 'myLoyaltyProgram',
        meta: {
          gtmId: 'GTM_PAGE_LOYALTY',
          pageName: ACCOUNT_LOYALTY_PAGE_NAME
        },
        component: () => import('../views/LoyaltyProgram.vue'),
        beforeEnter: () => {
          const savingsOrder = useSavingsStore()
          return savingsOrder.loadSavings()
        }
      },
      {
        path: 'my-partners',
        name: 'myPartners',
        meta: {
          gtmId: 'GTM_PAGE_PARTNERS',
          pageName: ACCOUNT_YOUR_PARTNER_BENEFITS_PAGE_NAME
        },
        component: () => import('../views/MyPartners.vue'),
        beforeEnter: async (to, from, next) => {
          const userStore = useUserStore()
          const { coBrandedCards, hasAutoConversion, userRawPromise } = storeToRefs(userStore)
          await userRawPromise.value

          if (!coBrandedCards.value?.length && !hasAutoConversion.value) {
            return next({ name: 'myLoyaltyProgram' })
          }

          return next()
        }
      },
      {
        path: 'security-settings',
        name: 'security-settings',
        meta: {
          title: 'securitySettings.page.title',
          gtmId: 'GTM_PAGE_SECURITY_SETTINGS',
          pageName: SECURITY_SETTINGS_PAGE_NAME
        },
        component: () => import('../views/SecuritySettings.vue')
      },
      {
        name: 'mySubscriptions',
        path: 'my-subscriptions',
        component: () => import('../views/SubscriptionsLayout.vue'),
        redirect: { name: 'mySubscriptionsHub' },
        children: [
          {
            path: '',
            name: 'mySubscriptionsHub',
            meta: {
              gtmId: 'GTM_PAGE_HUB_SUBSCRIPTION',
              pageName: ACCOUNT_HUB_SUBSCRIPTION_PAGE_NAME
            },
            component: () => import('../views/MySubscriptions.vue'),
            beforeEnter: async (to, from, next) => {
              const userStore = useUserStore()
              const { userHasAtLeastOneSubscriptionCard, userRawPromise } = storeToRefs(userStore)
              await userRawPromise.value

              if (!userHasAtLeastOneSubscriptionCard.value) {
                return next({ name: 'myLoyaltyProgram' })
              }

              return next()
            }
          },
          {
            path: 'all-signature/change-plan',
            name: 'allsignature-change-plan',
            meta: {
              title: 'card.title',
              gtmId: 'GTM_PAGE_CHANGE_PLAN',
              pageName: ACCOUNT_ALL_SIGNATURE_PLAN_MODIFICATION_PAGE_NAME
            },
            component: () => import('../views/AllSignatureChangePlan.vue'),
            beforeEnter: async (to, from, next) => {
              const userStore = useUserStore()
              const { activeAllSignatureSubscription, userRawPromise } = storeToRefs(userStore)
              await userRawPromise.value

              if (activeAllSignatureSubscription.value?.change_to_pricing_option_id) {
                return next({
                  name: 'subscription-card-details',
                  params: { cardName: 'all-signature' }
                })
              } else if (!activeAllSignatureSubscription.value) {
                return next({ name: 'myLoyaltyProgram' })
              }
              return next()
            }
          },
          {
            path: ':cardName',
            name: 'subscription-card-details',
            component: () => import('../views/SubscriptionCardDetails.vue'),
            beforeEnter: async (to, from, next) => {
              const userStore = useUserStore()
              const savingsOrder = useSavingsStore()
              const { getSubscriptionCardByName } = useUserStore()
              const { userRawPromise } = storeToRefs(userStore)
              await userRawPromise.value
              const subscriptionCardName = to.params.cardName as SUBSCRIPTION_NAME
              const subscriptionCard = getSubscriptionCardByName(subscriptionCardName)
              if (!subscriptionCard) {
                return next({ name: 'myLoyaltyProgram' })
              }
              await savingsOrder.loadSavings()
              return next()
            }
          }
        ]
      },
      {
        path: 'credential',
        name: 'credential',
        component: () => import('../views/Credential.vue'),
        meta: {
          gtmId: 'GTM_PAGE_CREDENTIALS',
          pageName: ACCOUNT_PROFILE_IDENTITY_DOCUMENTS_PAGE_NAME
        }
      },
      // Vue 2 routes
      {
        path: 'dashboard',
        name: 'dashboard',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'partner/airfranceklm',
        name: 'partner-airfranceklm',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'partner/qatar',
        name: 'partner-qatar',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'partner/qantas',
        name: 'partner-qantas',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'preferences',
        name: 'preferences',
        meta: {
          gtmId: 'GTM_PAGE_PREFERENCES',
          pageName: ACCOUNT_STAY_PREFERENCES_PAGE_NAME
        },
        component: () => import('../views/Preferences.vue')
      },
      {
        path: 'rewards',
        name: 'rewards',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'myLoyaltyProgram',
          params: { locale: to.params.locale }
        })
      },
      {
        path: 'status',
        name: 'status',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'myLoyaltyProgram',
          params: { locale: to.params.locale }
        })
      },
      {
        path: 'subscription-card/all-signature/change-plan',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'allsignature-change-plan',
          params: { locale: to.params.locale }
        })
      },
      {
        path: 'subscription-card/:cardName',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'subscription-card-details',
          params: { cardName: to.params.cardName, locale: to.params.locale }
        })
      },
      {
        path: 'payment-card/:card',
        name: 'payment-card',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'newsletter',
        name: 'newsletter',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'information/personal',
        name: 'information-personal',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'my-information',
          params: { locale: to.params.locale }
        })
      },
      {
        path: 'information/pro',
        name: 'information-pro',
        redirect: (to: RouteLocationGeneric) => ({
          name: 'my-information',
          params: { locale: to.params.locale }
        })
      },
      {
        path: 'my-information',
        name: 'my-information',
        meta: {
          gtmId: 'GTM_PAGE_INFORMATION',
          pageName: ACCOUNT_MY_INFORMATION_PAGE_NAME
        },
        component: () => import('../views/MyInformation.vue')
      },

      {
        path: 'wallet',
        name: 'wallet',
        redirect: '',
        meta: { isVue2: true }
      },
      {
        path: 'bookingPreferences',
        name: 'bookingPreferences',
        redirect: '',
        meta: { isVue2: true }
      },
      ...aabRoutes
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' }

    return to.name !== from.name ? { top: 0, behavior: 'smooth' } : false
  }
})

router.afterEach(
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded,
    failure?: NavigationFailure | void
  ) => {
    try {
      if (to.name !== from.name && !failure) {
        trackPage(to, { route: to })
      }
    } catch (e) {
      console.warn('router - trackPage failed on navigation', e, to)
    }
  }
)

export default router
